import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Button, Tooltip, Modal, Drawer, Space, Input, Spin, Tag, Alert, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined, HolderOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { resolveImgUrl } from '../../templates/4/utils';
import { RichTextEditor } from '../../shared/components/RichTextEditor';
import { isEmptyHtml } from '../../shared/components/RichContent';
import { ItemMediaSlot } from './ItemMediaSlot';
import { hexAlpha } from '../../utils/colorUtils';

const { Text } = Typography;

const CircleBtn = ({ icon, onClick, danger, title, disabled }) => (
  <Tooltip title={title}>
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        width: 32, height: 32, border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
        borderRadius: '50%', background: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: danger ? '#ef4444' : '#374151', opacity: disabled ? 0.45 : 1,
        transition: 'border-color 0.15s', fontSize: 14, flexShrink: 0,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >
      {icon}
    </button>
  </Tooltip>
);

const EMPTY_FORM = { pregunta: '', respuesta: '', icono: '❓', imagen: '', keywords: '' };

// Splitea por coma y/o espacios, descarta vacíos.
const splitTokens = (raw) => raw.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);

function KeywordTagsInput({ value, onChange, placeholder }) {
  const tags = value ? splitTokens(value) : [];
  const [draft, setDraft] = useState('');

  const addTags = (newTags) => {
    if (newTags.length === 0) return;
    const seen = new Set(tags.map(t => t.toLowerCase()));
    const merged = [...tags];
    newTags.forEach(t => {
      const key = t.toLowerCase();
      if (!seen.has(key)) { seen.add(key); merged.push(t); }
    });
    onChange(merged.join(', '));
  };

  const removeTag = (idx) => onChange(tags.filter((_, i) => i !== idx).join(', '));

  const flushDraft = () => {
    if (draft.trim()) { addTags(splitTokens(draft)); setDraft(''); }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    // Terminó en coma o espacio: confirma como badge(s) y limpia el input.
    if (/[,\s]$/.test(v)) {
      addTags(splitTokens(v));
      setDraft('');
    } else {
      setDraft(v);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      flushDraft();
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div>
      <Input
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={flushDraft}
        placeholder={placeholder}
      />
      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {tags.map((t, i) => (
            <Tag key={`${t}-${i}`} closable onClose={(e) => { e.preventDefault(); removeTag(i); }} style={{ margin: 0 }}>
              {t}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

export const AdminFaqs = () => {
  const { theme, config } = useClient();
  const paginaVisible = config?.uiConfig?.t4?.faqPage?.visible === true;
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = cerrado, {} = nuevo, {...} = edición
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const dragId = useRef(null);
  const [dragSrcId, setDragSrcId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const res = await api.faq.admin.getAll();
    if (res.status === 'success') setFaqs((res.data || []).sort((a, b) => a.orden - b.orden || a.id - b.id));
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing({}); };
  const openEdit = (f) => {
    setForm({ pregunta: f.pregunta, respuesta: f.respuesta || '', icono: f.icono || '', imagen: f.imagen || '', keywords: f.keywords || '' });
    setEditing(f);
  };

  const handleSave = async () => {
    if (!form.pregunta.trim()) { message.error('La pregunta es obligatoria'); return; }
    setSaving(true);
    const payload = { ...form, respuesta: isEmptyHtml(form.respuesta) ? '' : form.respuesta };
    try {
      // El orden lo maneja el backend: una nueva va al final.
      const res = editing?.id
        ? await api.faq.admin.editar(editing.id, payload)
        : await api.faq.admin.crear(payload);
      if (res.status === 'success') {
        message.success(editing?.id ? 'Pregunta actualizada' : 'Pregunta creada');
        setEditing(null);
        cargar();
      } else {
        message.error(res.message || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.faq.admin.eliminar(confirmDel.id);
      message.success('Pregunta eliminada');
      setConfirmDel(null);
      cargar();
    } finally {
      setDeleting(false);
    }
  };

  const handleToggle = async (f) => {
    if (toggling) return;
    setToggling(f.id);
    try {
      await api.faq.admin.editar(f.id, { activo: !f.activo });
      await cargar();
    } finally {
      setToggling(null);
    }
  };

  // --- Drag & drop (reorder, optimistic update) ---
  const handleDragStart = (e, f) => {
    dragId.current = f.id;
    setDragSrcId(f.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, f) => {
    e.preventDefault();
    if (f.id !== dragId.current) setDragOverId(f.id);
  };
  const handleDragLeave = () => setDragOverId(null);
  const handleDragEnd = () => { dragId.current = null; setDragSrcId(null); setDragOverId(null); };

  const handleDrop = async (e, target) => {
    e.preventDefault();
    setDragOverId(null);
    setDragSrcId(null);
    const srcId = dragId.current;
    dragId.current = null;
    if (!srcId || srcId === target.id) return;

    const srcIdx = faqs.findIndex(f => f.id === srcId);
    const tgtIdx = faqs.findIndex(f => f.id === target.id);
    if (srcIdx === -1 || tgtIdx === -1) return;

    const reordered = [...faqs];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);

    const withOrden = reordered.map((f, idx) => ({ ...f, orden: idx * 10 }));
    setFaqs(withOrden);

    // Un solo request con el orden completo, no uno por pregunta.
    const res = await api.faq.admin.ordenar(withOrden.map(f => f.id)).catch(() => null);
    if (res?.status !== 'success') {
      message.error('Error al guardar el orden');
      cargar();
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Preguntas Frecuentes</Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}
          style={{ background: primary, borderColor: primary, color: primaryText }}>
          Nueva pregunta
        </Button>
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        Cargá las preguntas que van a mostrarse en la página pública /faq de tu tienda. Arrastrá para reordenarlas. El aspecto de la página (título, colores) se configura en el diseño de la tienda → Preguntas frecuentes.
      </Text>

      {!paginaVisible && (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message="La página de preguntas frecuentes está oculta"
          description="Las preguntas se pueden ir cargando igual. Para que los clientes la vean, activala en Config. Landing → Preguntas frecuentes."
        />
      )}

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
        {loading && (
          <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
        )}
        {!loading && faqs.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <QuestionCircleOutlined style={{ fontSize: 36, color: '#d1d5db', marginBottom: 12, display: 'block' }} />
            <Text type="secondary" style={{ fontSize: 14 }}>
              No hay preguntas todavía. Creá una para tu página de preguntas frecuentes.
            </Text>
          </div>
        )}
        {!loading && faqs.map((f, idx) => {
          const isLast = idx === faqs.length - 1;
          const isDragOver = dragOverId === f.id;
          const isToggling = toggling === f.id;
          return (
            <div
              key={f.id}
              draggable
              onDragStart={(e) => handleDragStart(e, f)}
              onDragOver={(e) => handleDragOver(e, f)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, f)}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
                borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
                background: isDragOver ? hexAlpha(primary, 0.06) : '#fff',
                borderTop: isDragOver ? `2px solid ${primary}` : undefined,
                opacity: dragSrcId === f.id ? 0.4 : (f.activo ? 1 : 0.55),
                transition: 'background 0.1s, opacity 0.1s',
              }}
            >
              <HolderOutlined style={{ color: '#d1d5db', cursor: 'grab', fontSize: 15, flexShrink: 0 }} />
              <div style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                background: '#f0f2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
              }}>
                {f.imagen ? <img src={resolveImgUrl(f.imagen)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (f.icono || '❓')}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: 14, color: primary, cursor: 'pointer',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }} onClick={() => openEdit(f)}>
                  {f.pregunta}
                </div>
                {f.keywords && <Text type="secondary" style={{ fontSize: 12 }}>{f.keywords}</Text>}
              </div>
              <Tooltip title={isToggling ? '' : (f.activo ? 'Doble click para ocultar' : 'Doble click para mostrar')}>
                <span
                  onDoubleClick={() => handleToggle(f)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 20,
                    border: `1.5px solid ${f.activo ? '#16a34a' : '#9ca3af'}`,
                    background: f.activo ? '#f0fdf4' : '#f9fafb',
                    color: f.activo ? '#16a34a' : '#9ca3af',
                    fontSize: 12, fontWeight: 600, cursor: isToggling ? 'default' : 'pointer',
                    userSelect: 'none', opacity: isToggling ? 0.6 : 1, flexShrink: 0,
                  }}
                >
                  {f.activo ? 'Visible' : 'Oculta'}
                </span>
              </Tooltip>
              <Space size={6}>
                <CircleBtn icon={<EditOutlined />} title="Editar" onClick={() => openEdit(f)} />
                <CircleBtn icon={<DeleteOutlined />} title="Eliminar" danger onClick={() => setConfirmDel(f)} />
              </Space>
            </div>
          );
        })}
      </div>

      <Drawer
        open={!!editing}
        title={editing?.id ? 'Editar pregunta' : 'Nueva pregunta'}
        onClose={() => !saving && setEditing(null)}
        placement="right"
        width={560}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button type="primary" onClick={handleSave} loading={saving}
              style={{ background: primary, borderColor: primary, color: primaryText }}>
              Guardar
            </Button>
          </Space>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div>
              <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Ícono</Text>
              <ItemMediaSlot
                item={{ icon: form.icono, image: form.imagen }}
                onUpdate={patch => setForm(f => ({
                  ...f,
                  icono: patch.icon !== undefined ? patch.icon : f.icono,
                  imagen: patch.image !== undefined ? patch.image : f.imagen,
                }))}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Pregunta</Text>
              <Input value={form.pregunta} onChange={e => setForm(f => ({ ...f, pregunta: e.target.value }))}
                placeholder="¿Cuánto tarda el envío?" />
            </div>
          </div>
          <div>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Respuesta</Text>
            <RichTextEditor
              value={form.respuesta}
              onChange={html => setForm(f => ({ ...f, respuesta: html }))}
              placeholder="Escribí la respuesta..."
            />
          </div>
          <div>
            <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Palabras clave</Text>
            <KeywordTagsInput
              value={form.keywords}
              onChange={v => setForm(f => ({ ...f, keywords: v }))}
              placeholder="Escribí y presioná espacio, coma o enter..."
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              Se muestran como filtros (badges) en la página pública, además de usarse para el buscador.
            </Text>
          </div>
        </div>
      </Drawer>

      <Modal
        open={!!confirmDel}
        title="¿Eliminar pregunta?"
        onCancel={() => !deleting && setConfirmDel(null)}
        footer={
          <Space>
            <Button onClick={() => setConfirmDel(null)} disabled={deleting}>Cancelar</Button>
            <Button danger onClick={handleDelete} loading={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </Space>
        }
      >
        {confirmDel && <Text><strong>{confirmDel.pregunta}</strong> será eliminada.</Text>}
      </Modal>
    </div>
  );
};
